<script setup lang="ts">
/**
 * How diamond purchases are paid — the core payments method picker, in the
 * shop's own look.
 *
 * Methods come from `GET /api/payments/methods` (only the ones an admin enabled
 * and whose terminal is configured). A method that cannot charge in the
 * player's currency (BLIK → PLN, Apple Pay / Open Banking → EUR) asks first:
 * confirming switches the player's currency, and the shop re-prices in it.
 * Apple Pay / Open Banking also need a complete billing address — the server
 * refuses with BILLING_ADDRESS_REQUIRED and the shop shows that message.
 */
import { nextTick, onMounted, ref, watch } from 'vue';

import { ApiError } from '@/net/api';
import { usePayments, type CurrencySwitch } from '@/state/payments';

const props = defineProps<{ currency: string }>();
const emit = defineEmits<{ currencyChanged: [currency: string] }>();

const payments = usePayments();
const pending = ref<CurrencySwitch | null>(null);
const busy = ref(false);
const error = ref<string | null>(null);

onMounted(() => {
  if (!payments.loaded.value) void payments.load();
});

// The player changed currency elsewhere (Account pane) or the methods loaded:
// a selected method that cannot charge in it asks for the switch right away.
watch(
  () => [props.currency, payments.selected.value?.id] as const,
  ([, id]) => {
    if (id && !pending.value) pending.value = payments.choose(id, props.currency);
  },
  { immediate: true },
);

function pick(id: string): void {
  error.value = null;
  pending.value = payments.choose(id, props.currency);
  if (pending.value) {
    void nextTick(() => {
      document.querySelector('[data-test="currency-switch"]')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  }
}

async function confirm(): Promise<void> {
  if (!pending.value) return;
  busy.value = true;
  try {
    const to = pending.value.to;
    await payments.confirmSwitch(pending.value);
    pending.value = null;
    emit('currencyChanged', to);
  } catch (err) {
    error.value = err instanceof ApiError ? err.message : 'Could not change your currency.';
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <section class="methods" data-test="payment-methods" aria-label="Payment method">
    <span class="eyebrow">Pay with</span>
    <p v-if="payments.loaded.value && payments.methods.value.length === 0" class="note">
      {{ payments.loadError.value ?? 'Payments are temporarily unavailable.' }}
    </p>
    <div v-else class="row" role="radiogroup">
      <button
        v-for="m in payments.methods.value"
        :key="m.id"
        type="button"
        role="radio"
        class="method"
        :class="{ active: payments.selected.value?.id === m.id }"
        :aria-checked="payments.selected.value?.id === m.id"
        :data-method="m.id"
        @click="pick(m.id)"
      >
        <span>{{ m.label }}</span>
        <small>{{ m.currencies.length === 1 ? m.currencies[0] : props.currency }}</small>
      </button>
    </div>
    <div v-if="pending" class="confirm" role="alertdialog" data-test="currency-switch">
      <p>
        {{ pending.method.label }} charges in <strong>{{ pending.to }}</strong>.
        Switch your prices from {{ pending.from }} to {{ pending.to }}?
      </p>
      <div class="actions">
        <button type="button" class="plate" :disabled="busy" data-action="confirm-currency-switch" @click="confirm">
          Switch to {{ pending.to }}
        </button>
        <button type="button" class="plate ghost" :disabled="busy" @click="pending = null">Cancel</button>
      </div>
    </div>
    <p v-if="error" class="note error">{{ error }}</p>
  </section>
</template>

<style scoped>
.methods {
  display: grid;
  gap: 0.5rem;
  margin: 0 0 1rem;
  padding: 0.75rem 1rem;
  border: 1px solid var(--steel-600, #2c3440);
  background: var(--steel-900, #0f1318);
}
.eyebrow {
  font-family: var(--font-display, inherit);
  font-size: 0.75rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--steel-300, #9aa6b4);
}
.row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.method {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 44px;
  padding: 0.4rem 0.9rem;
  border: 1px solid var(--steel-600, #2c3440);
  background: var(--steel-800, #161b22);
  color: var(--steel-200, #c8d1db);
  font: inherit;
  cursor: pointer;
}
.method small {
  color: var(--steel-400, #7c8896);
  font-family: var(--font-mono, monospace);
}
.method.active {
  border-color: var(--accent, #e8722c);
  color: #fff;
  box-shadow: inset 0 0 0 1px var(--accent, #e8722c);
}
.confirm {
  display: grid;
  gap: 0.5rem;
  padding: 0.75rem;
  border-left: 3px solid var(--accent, #e8722c);
  background: var(--steel-800, #161b22);
}
.confirm p {
  margin: 0;
}
.actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.plate {
  min-height: 44px;
  padding: 0.4rem 1rem;
  border: 1px solid var(--accent, #e8722c);
  background: var(--accent, #e8722c);
  color: #0b0d11;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}
.plate.ghost {
  background: transparent;
  color: var(--steel-200, #c8d1db);
  border-color: var(--steel-600, #2c3440);
}
.plate:disabled {
  opacity: 0.6;
  cursor: default;
}
.note {
  margin: 0;
  color: var(--steel-300, #9aa6b4);
}
.note.error {
  color: #ff8a7a;
}
</style>
